import React from "react";
import { Alert, Grid } from "@mui/material";
import { Formik } from "formik";
import { formatCurrency } from "@poseidon/utils/currency";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { useTimeMachine } from "@poseidon/firebase/hooks/useTimeMachine";
import LinearProgress from "@mui/material/LinearProgress";
import { useReschedule } from "./useReschedule";
import { useReschedulePreview } from "./useReschedulePreview";
import { ReschedulePreview } from "./ReschedulePreview";
import { RescheduleForm } from "./RescheduleForm";
import { Panel } from "@poseidon/ui/components/Panel";
import {
  FormikDisabler,
  usePermissions,
} from "@poseidon/ui/components";

export const Reschedule = ({ schedule }) => {
  const { canWrite } = usePermissions();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const { timeMachine, loading: tmLoading } = useTimeMachine();
  const { reschedule } = useReschedule(schedule.id);
  const { reschedulePreview, preview, loading } = useReschedulePreview(
    schedule.id
  );
  if (tmLoading) {
    return <LinearProgress />;
  }
  // Time machine is a dev-only testing aid; when its singleton doc is absent
  // (e.g. production) fall back to the real current date.
  const now = timeMachine?.now ?? new Date();
  const onSubmitHandler = async (values) => {
    try {
      reschedulePreview(values).then((success) => {
        if (!success) {
          setError("Unknown error");
        }
      });
    } catch (error) {
      setError(error.message);
    }
  };
  const applyReschedule = async (values) => {
    try {
      reschedule(values).then(({ success }) => {
        if (success) {
          setToast("Rescheduled");
        } else {
          setError("Unknown error");
        }
      });
    } catch (error) {
      setError(error.message);
    }
  };
  const isFixedRate = Boolean(schedule.parameters.fixedRate);
  const isBalloon = schedule.parameters.balloon > 0;
  const initialValues = {
    mode: "preserve",
    baseRate: schedule.parameters.baseRate,
    addonRate: schedule.parameters.addonRate,
    term: schedule.payments?.filter((p) => p.date > now).length,
    repayment: (
      Math.round(Math.abs(schedule.parameters.monthlyPayment) * 100) / 100
    ).toFixed(2),
    // pre-fill from the current schedule so the balloon is preserved unless
    // the admin changes it; blank for non-balloon schedules (omitted on send)
    balloon: schedule.parameters.balloon > 0 ? schedule.parameters.balloon : "",
    holidayMonths: 0,
    lumpSum: 0,
  };
  // Mirror the backend rule that an overriding balloon must be non-negative.
  const validate = (values) => {
    const errors = {};
    if (values.balloon !== "" && values.balloon != null) {
      const balloon = Number(values.balloon);
      if (Number.isNaN(balloon)) {
        errors.balloon = "Enter a number";
      } else if (balloon < 0) {
        errors.balloon = "Balloon must not be negative";
      }
    }
    return errors;
  };
  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmitHandler}
      enableReinitialize
    >
      <>
        <FormikDisabler disabled={!canWrite} />
        <Panel title="Reschedule" expandable>
          {isFixedRate && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This is a fixed-rate agreement. It is excluded from automatic
              base-rate-change reschedules; any reschedule here is a manual
              change to the customer's agreed terms.
            </Alert>
          )}
          {isBalloon && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This is a balloon agreement. The balloon (
              {formatCurrency(schedule.parameters.balloon)}) will be carried
              forward as the final instalment unless you override it.
            </Alert>
          )}
          <Grid container spacing={5}>
            <Grid item xs={12} md={6}>
              <RescheduleForm />
            </Grid>
            <Grid item xs={12} md={6}>
              {loading && <LinearProgress />}
              {preview && (
                <ReschedulePreview data={preview} onApply={applyReschedule} />
              )}
            </Grid>
          </Grid>
        </Panel>
      </>
    </Formik>
  );
};
